import { useTranslation } from 'react-i18next'
import React from 'react'
import { Box, Stack, Typography } from '@mui/material'
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded'

import { BOX_SHADOW_XS, GRAY_300, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import propertyPlaceholder from 'assets/img/property/property-placeholder.webp'
import { MUIIconButton } from 'components/common/MUIIconButton'

interface Props {
  /** Property display name */
  name: string
  /** Property address string */
  address: string
  /** Callback to remove property from selection. Omitted, the card offers no remove — the embed's property is fixed by the CRM. */
  onRemove?: () => void
}

/**
 * Selected-property card shown below the search input in the PropertySelectionDrawer.
 * Displays thumbnail, name, address, and a remove button.
 *
 * Ported from pre-Phase 0 reference: PropertySelectionItem.component.reference.tsx
 */
export const PropertySelectionItem: React.FC<Props> = ({ name, address, onRemove }) => {
  const { t } = useTranslation(['common'])
  return (
    <Stack
      direction="row"
      gap="1.2rem"
      alignItems="center"
      sx={{
        backgroundColor: 'white',
        border: `1px solid ${GRAY_300}`,
        borderRadius: '0.8rem',
        padding: '1.2rem',
        boxShadow: BOX_SHADOW_XS
      }}
    >

      <Stack direction="row" flex="1 1 auto" gap="1.6rem" alignItems="center" overflow="hidden">

        <Box
          flexShrink={0}
          width="4.8rem"
          height="4.8rem"
          borderRadius="0.4rem"
          overflow="hidden"
          sx={{ boxShadow: BOX_SHADOW_XS }}
        >
          <img
            alt="property"
            src={propertyPlaceholder}
            style={{ width: '4.8rem', height: '4.8rem', display: 'block', objectFit: 'cover', borderRadius: '0.4rem' }}
          />
        </Box>

        <Stack gap="0.2rem" overflow="hidden">

          <Typography
            variant="text-sm"
            color={GRAY_900}
            fontWeight="semiBold"
            noWrap
          >
            {name}
          </Typography>

          <Typography
            variant="text-xs"
            color={GRAY_700}
            noWrap
          >
            {address}
          </Typography>

        </Stack>

      </Stack>

      {onRemove &&
      <MUIIconButton variant="plain" icon={<DeleteOutlineRoundedIcon />} ariaLabel={t('common:Remove')} onClick={onRemove} />
      }

    </Stack>
  )
}
